import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Send, Loader2, User, Search, MessageCircle, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

type Conversation = {
  id: string;
  user_id: string;
  title: string;
  subject: string | null;
  updated_at: string;
};

type ConvSummary = {
  conv_id: string;
  user_id: string;
  user_name: string;
  title: string;
  subject: string | null;
  last_at: string;
};

type ChatMessage = {
  id: string;
  role: string;
  content: string;
  created_at: string;
  user_id: string;
  conversation_id: string;
};

interface AdminChatViewProps {
  onClose: () => void;
}

const AdminChatView = ({ onClose }: AdminChatViewProps) => {
  const [conversations, setConversations] = useState<ConvSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedConv, setSelectedConv] = useState<ConvSummary | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 50);
  }, []);

  const loadConversations = useCallback(async () => {
    setLoading(true);
    const { data: convs } = await supabase
      .from("conversations")
      .select("*")
      .order("updated_at", { ascending: false })
      .limit(200);

    if (!convs || convs.length === 0) { setConversations([]); setLoading(false); return; }

    const userIds = [...new Set(convs.map((c: Conversation) => c.user_id))];
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, full_name")
      .in("id", userIds);

    const nameMap = new Map<string, string>();
    for (const p of profiles || []) nameMap.set(p.id, p.full_name || "Utilisateur");

    const summaries: ConvSummary[] = convs.map((c: Conversation) => ({
      conv_id: c.id,
      user_id: c.user_id,
      user_name: nameMap.get(c.user_id) || "Utilisateur",
      title: c.title,
      subject: c.subject,
      last_at: c.updated_at,
    }));

    setConversations(summaries);
    setLoading(false);
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Load messages for selected conversation
  useEffect(() => {
    if (!selectedConv) return;
    setLoadingMsgs(true);
    supabase
      .from("messages")
      .select("*")
      .eq("conversation_id", selectedConv.conv_id)
      .order("created_at", { ascending: true })
      .limit(500)
      .then(({ data }) => {
        setMessages((data as ChatMessage[]) || []);
        setLoadingMsgs(false);
        scrollToBottom();
      });
  }, [selectedConv, scrollToBottom]);

  // Realtime for new messages in selected conversation
  useEffect(() => {
    if (!selectedConv) return;
    const channel = supabase
      .channel(`admin-conv-${selectedConv.conv_id}`)
      .on("postgres_changes", {
        event: "INSERT", schema: "public", table: "messages",
        filter: `conversation_id=eq.${selectedConv.conv_id}`,
      }, (payload) => {
        const msg = payload.new as ChatMessage;
        setMessages((prev) => prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]);
        scrollToBottom();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [selectedConv, scrollToBottom]);

  const handleSend = async () => {
    if (!input.trim() || sending || !selectedConv) return;
    setSending(true);
    const text = input.trim();
    setInput("");

    // Insert as "assistant" role so it appears as AI response in user's chat
    const { error } = await supabase.from("messages").insert({
      conversation_id: selectedConv.conv_id,
      user_id: selectedConv.user_id,
      role: "assistant",
      content: text,
    });

    setSending(false);
    if (error) setInput(text);
    else scrollToBottom();
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) + " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  const filtered = search
    ? conversations.filter((c) => c.user_name.toLowerCase().includes(search.toLowerCase()) || c.title.toLowerCase().includes(search.toLowerCase()))
    : conversations;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-primary/5">
        <div className="flex items-center gap-2">
          {selectedConv && (
            <button onClick={() => setSelectedConv(null)} className="p-1 rounded-lg hover:bg-muted">
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <div>
            <p className="text-sm font-bold text-foreground">
              {selectedConv ? selectedConv.user_name : "💬 Conversations IA"}
            </p>
            <p className="text-[10px] text-muted-foreground">
              {selectedConv ? (selectedConv.title || "Conversation") : "Mode Super Admin"}
            </p>
          </div>
        </div>
        {!selectedConv && (
          <button onClick={loadConversations} className="p-2 rounded-xl hover:bg-muted text-muted-foreground mr-8">
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
      </div>

      {!selectedConv ? (
        /* Conversation list */
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher..."
                className="pl-9 h-9 text-xs rounded-xl"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && (
              <div className="flex justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-6">Aucune conversation</p>
            )}
            {filtered.map((c) => (
              <button
                key={c.conv_id}
                onClick={() => setSelectedConv(c)}
                className="w-full text-left px-3 py-2.5 border-b border-border/50 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <User className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-foreground truncate">{c.user_name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{c.title}</p>
                  </div>
                  {c.subject && (
                    <Badge variant="secondary" className="text-[9px] px-1.5 py-0 flex-shrink-0">
                      {c.subject}
                    </Badge>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5 pl-9">{formatTime(c.last_at)}</p>
              </button>
            ))}
          </div>
        </div>
      ) : (
        /* Messages + Input */
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5">
            {loadingMsgs && (
              <div className="flex justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {messages.map((msg) => (
              <div key={msg.id} className={`flex gap-2 ${msg.role === "user" ? "" : "flex-row-reverse"}`}>
                {msg.role === "user" && (
                  <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User className="w-3 h-3 text-muted-foreground" />
                  </div>
                )}
                {msg.role === "assistant" && (
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MessageCircle className="w-3 h-3 text-primary" />
                  </div>
                )}
                <div
                  className={`rounded-2xl px-3 py-2 max-w-[80%] ${
                    msg.role === "user"
                      ? "bg-muted text-foreground rounded-tl-md"
                      : "bg-primary text-primary-foreground rounded-tr-md"
                  }`}
                >
                  <p className="text-xs whitespace-pre-wrap break-words">{msg.content}</p>
                  <p className={`text-[9px] mt-0.5 ${msg.role === "user" ? "text-muted-foreground" : "text-primary-foreground/60"}`}>
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Admin reply input */}
          <div className="border-t border-border px-3 py-2 bg-card">
            <p className="text-[9px] text-amber-600 font-medium mb-1">⚠️ Répondre en tant qu'IA (visible par l'utilisateur)</p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Répondre à cet utilisateur..."
                className="flex-1 bg-muted rounded-2xl px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground outline-none min-h-[38px]"
                maxLength={4000}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || sending}
                className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40 transition-opacity flex-shrink-0"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminChatView;
